import { useEffect, useRef, useState, useCallback } from "react";
import { Menu, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { Composer } from "../components/Composer";
import { ModelPicker } from "../components/ModelPicker";
import { PlanDialog } from "../components/PlanDialog";
import { KidsDialog } from "../components/KidsDialog";
import { TurnView } from "../components/TurnView";
import { DebateToggle } from "../components/KidsDebate";
import { OneMoreRound, SurpriseButton, randomTopic } from "../components/DebateExtras";
import { QuickReplies } from "../components/QuickReplies";
import { MyWordsDialog } from "../components/MyWordsDialog";
import { BillingDialog } from "../components/BillingDialog";
import { ListenMode, ListenButton } from "../components/ListenMode";
import { SafetyIntro } from "../components/SafetyIntro";
import { EmptyState } from "../components/EmptyState";
import { listChats, getChat, deleteChat, askStream, words } from "../lib/api";
import { markSaved } from "../components/WordBalloon";
import { MODELS } from "../lib/models";
import { useAuth } from "../context/AuthContext";

const DEFAULT_PREFS = { models: MODELS.map((m) => m.key), merger: "claude" };
const loadPrefs = () => {
  try {
    const p = JSON.parse(localStorage.getItem("what-prefs"));
    if (p?.models?.length && p.merger) return p;
  } catch {}
  return DEFAULT_PREFS;
};

const newLiveTurn = (question, prefs, kids) => ({
  question, live: true, phase: "models", final: "", error: "", agreement: null, agreement_after: null, kids,
  debate: {}, debateStatuses: {}, kids_debate: [], debating: !!prefs.debate,
  models: prefs.models, merger: prefs.merger,
  answers: Object.fromEntries(prefs.models.map((k) => [k, ""])),
  statuses: Object.fromEntries(prefs.models.map((k) => [k, "loading"])),
});

function applyEvent(turn, ev) {
  const t = { ...turn, answers: { ...turn.answers }, statuses: { ...turn.statuses }, debate: { ...turn.debate }, debateStatuses: { ...turn.debateStatuses } };
  const isDebate = ev.phase === "debate";
  if (ev.type === "delta" && ev.model === "final") t.final += ev.content;
  else if (ev.type === "delta" && isDebate) t.debate[ev.model] += ev.content;
  else if (ev.type === "delta") t.answers[ev.model] += ev.content;
  else if (ev.type === "model_done") (isDebate ? t.debateStatuses : t.statuses)[ev.model] = "done";
  else if (ev.type === "model_error") (isDebate ? t.debateStatuses : t.statuses)[ev.model] = "error";
  else if (ev.type === "agreement_start") t.phase = "agreement";
  else if (ev.type === "agreement") t.agreement = { score: ev.score, summary: ev.summary };
  else if (ev.type === "debate_start") {
    t.phase = "debate";
    t.debate = Object.fromEntries(ev.models.map((k) => [k, ""]));
    t.debateStatuses = Object.fromEntries(ev.models.map((k) => [k, "loading"]));
  } else if (ev.type === "agreement_after") t.agreement_after = { score: ev.score, summary: ev.summary };
  else if (ev.type === "merge_start") t.phase = "merge";
  else if (ev.type === "kids_check") t.phase = "kids";
  else if (ev.type === "kids_blocked") t.kids_blocked = true;
  else if (ev.type === "hard_words") t.hard_words = ev.words;
  else if (ev.type === "mind_changed") { t.mind_changed = ev.models; t.mind_reason = ev.reason; }
  else if (ev.type === "kids_round") t.phase = `kids${ev.round}`;
  else if (ev.type === "kids_msg") t.kids_debate = [...(t.kids_debate || []), ev.msg];
  else if (ev.type === "error") t.error = ev.message;
  else if (ev.type === "complete") t.live = false;
  return t;
}

export default function Workspace() {
  const { user, logout, refreshUser } = useAuth();
  const [billingOpen, setBillingOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [planOpen, setPlanOpen] = useState(false);
  const [kidsOpen, setKidsOpen] = useState(false);
  const [wordsOpen, setWordsOpen] = useState(false);
  const [listenOpen, setListenOpen] = useState(false);
  const kids = !!user?.kids_mode;
  const isPro = user?.plan === "pro";
  const [debateOn, setDebateOn] = useState(false);
  const composerRef = useRef(null);
  const [composerH, setComposerH] = useState(260);
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setComposerH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const effPrefs = { ...(isPro ? prefs : { ...prefs, models: prefs.models.slice(0, 1) }), debate: debateOn };
  const bottomRef = useRef(null);

  const refresh = useCallback(() => listChats().then(setChats).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (kids) words.list().then((l) => markSaved(l.map((w) => w.word))).catch(() => {}); }, [kids]);
  useEffect(() => { localStorage.setItem("what-prefs", JSON.stringify(prefs)); }, [prefs]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turns.length, busy]);

  const openChat = async (id) => {
    if (busy) { toast("Please wait for the current answer to finish"); return; }
    setSideOpen(false);
    try {
      const chat = await getChat(id);
      setActiveId(id);
      setTurns(chat.turns);
      setDebateOn(!!chat.turns[chat.turns.length - 1]?.kids_debate?.length);
    } catch {
      toast.error("Couldn't load that chat");
      refresh();
    }
  };

  const newChat = () => { if (busy) return; setActiveId(null); setTurns([]); setSideOpen(false); setDebateOn(false); };

  const removeChat = async (id) => {
    try {
      await deleteChat(id);
      if (id === activeId) newChat();
      toast.success("Chat deleted");
    } catch {
      toast.error("Couldn't delete that chat");
    }
    refresh();
  };

  const ask = async (question, extra = {}) => {
    const p = { ...effPrefs, ...extra };
    if (extra.debate) setDebateOn(true);
    setBusy(true);
    setTurns((prev) => [...prev, newLiveTurn(question, p, kids)]);
    const update = (ev) => setTurns((prev) => {
      const copy = [...prev];
      copy[copy.length - 1] = applyEvent(copy[copy.length - 1], ev);
      return copy;
    });
    try {
      await askStream(question, extra.newChat ? null : activeId, p, (ev) => {
        if (ev.type === "chat") { setActiveId(ev.chat_id); refresh(); }
        else update(ev);
      });
    } catch (e) {
      update({ type: "error", message: e.message || "Something went wrong. Please try again." });
      update({ type: "complete" });
    }
    setBusy(false);
    refresh();
    if (p.debate) refreshUser();
  };

  const kidsChanged = () => { setActiveId(null); setTurns([]); refresh(); };
  const surprise = () => {
    if (busy) return;
    setActiveId(null);
    setTurns([]);
    ask(randomTopic(kids), { debate: true, newChat: true });
  };
  const last = turns[turns.length - 1];
  const canMore = !busy && activeId && last && !last.live && last.kids_debate?.length > 0;

  return (
    <div className={`what-app flex h-screen overflow-hidden ${kids ? "is-kids" : ""}`}>
      <Sidebar chats={chats} activeId={activeId} onSelect={openChat} onNew={newChat} onDelete={removeChat} open={sideOpen} user={user} onLogout={logout} onPlan={() => setPlanOpen(true)} onKids={() => setKidsOpen(true)} onWords={() => setWordsOpen(true)} onBilling={() => setBillingOpen(true)} busy={busy} />
      {!kids && <BillingDialog open={billingOpen} onOpenChange={setBillingOpen} onPlan={() => { setBillingOpen(false); setPlanOpen(true); }} />}
      {kids && <MyWordsDialog open={wordsOpen} onOpenChange={setWordsOpen} />}
      {kids && <ListenMode open={listenOpen} onOpenChange={setListenOpen} />}
      {kids && user && !user.safety_intro_done && <SafetyIntro />}
      {!kids && <PlanDialog open={planOpen} onOpenChange={setPlanOpen} />}
      <KidsDialog open={kidsOpen} onOpenChange={setKidsOpen} onChanged={kidsChanged} />
      {sideOpen && <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setSideOpen(false)} />}
      <main className="flex-1 flex flex-col min-w-0 relative">
        <button data-testid="open-sidebar-btn" onClick={() => setSideOpen(true)} className="md:hidden absolute top-4 left-4 z-20 text-slate-300">
          <Menu size={22} />
        </button>
        {kids && (
          <div data-testid="kids-mode-banner" className="what-kids-banner pl-14 md:pl-4">
            <ShieldCheck size={15} className="shrink-0" /> Kids Mode is on<span className="hidden sm:inline"> · every question and answer is checked to be safe for children under 9</span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto" data-testid="chat-thread">
          <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-16" style={{ paddingBottom: composerH + 32 }}>
            {turns.length === 0
              ? <EmptyState onPick={ask} isPro={isPro} kids={kids} onKids={() => setKidsOpen(true)} onSurprise={surprise} />
              : turns.map((t, i) => <TurnView key={i} turn={t} index={i} chatId={activeId} />)}
            {canMore && <OneMoreRound onClick={() => ask("One more round", { debate: true, more: true })} />}
            <div ref={bottomRef} />
          </div>
        </div>
        <div ref={composerRef} className="absolute bottom-0 inset-x-0 what-composer-wrap">
          <div className="max-w-3xl mx-auto px-6 sm:px-10 pb-6">
            <ModelPicker prefs={effPrefs} setPrefs={setPrefs} disabled={busy} isPro={isPro} kids={kids || user?.role === "student"} onUpgrade={() => setPlanOpen(true)} />
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <DebateToggle on={debateOn} setOn={setDebateOn} disabled={busy} />
              <SurpriseButton onClick={surprise} disabled={busy} />
              {kids && <ListenButton onClick={() => setListenOpen(true)} disabled={busy} />}
              {debateOn && effPrefs.models.length < 2 && (
                <span data-testid="debate-pro-hint" className="text-xs text-slate-400">{isPro ? "Pick 2 or 3 AIs to watch them debate each other too." : "Free plan: 1 AI debates with you. Pro lets all 3 AIs debate each other."}</span>
              )}
            </div>
            {kids && turns.length > 0 && !busy && <QuickReplies onPick={ask} disabled={busy} />}
            <Composer onSubmit={ask} busy={busy} placeholder={debateOn ? (kids ? "Tell the AIs your idea, and they'll debate it with you!" : "Share an idea or claim, and the AIs will debate it with you") : kids ? "Ask me anything you're curious about!" : undefined} />
          </div>
        </div>
      </main>
    </div>
  );
}
